/**
 * 中转站令牌(OpenAI 兼容)管理 Tab — 对接 /api/relay/tokens/my 与 /api/relay/usage/my
 * 功能:列表 / 创建(明文仅显示一次)/ 吊销 / 用量概览
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, Tag, message, Modal, Input, Space, Typography, Alert, Tooltip, Statistic, Row, Col, Empty } from 'antd';
import { KeyOutlined, PlusOutlined, DeleteOutlined, CopyOutlined, BarChartOutlined } from '@ant-design/icons';
import { apiClient, extractApiError } from '@/services/api';

const { Paragraph } = Typography;

interface RelayTokenInfo {
  id: string;
  label: string;
  quotaTotal: number;
  quotaUsed: number;
  status: string;
  expireAt: string | null;
  createdAt: string;
}

interface RelayUsageRow {
  _id: string;
  used: number;
  calls: number;
}

const RELAY_BASE_URL = 'https://aibak.site/api/relay/v1';

const RelayTokensTab: React.FC = () => {
  const [tokens, setTokens] = useState<RelayTokenInfo[]>([]);
  const [usage, setUsage] = useState<RelayUsageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [newTokenPlain, setNewTokenPlain] = useState('');
  const [newTokenModalOpen, setNewTokenModalOpen] = useState(false);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get('/relay/tokens/my');
      setTokens(res.data || []);
    } catch (e) {
      message.error(extractApiError(e, '加载中转站令牌失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const res: any = await apiClient.get('/relay/usage/my');
      setUsage(res.data || []);
    } catch (e) {
      // 用量加载失败不阻塞主功能
      console.warn('中转站用量加载失败', e);
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
    loadUsage();
  }, [loadTokens, loadUsage]);

  const createToken = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const res: any = await apiClient.post('/relay/tokens/my', { label: newLabel.trim() || undefined });
      setNewTokenPlain(res.data.token);
      setCreateModalOpen(false);
      setNewLabel('');
      setNewTokenModalOpen(true);
      loadTokens();
    } catch (e) {
      message.error(extractApiError(e, '创建失败'));
    } finally {
      setCreating(false);
    }
  };

  const revokeToken = (id: string) => {
    Modal.confirm({
      title: '确认吊销此令牌？',
      content: '吊销后所有使用该令牌的请求将被拒绝，无法恢复。',
      onOk: async () => {
        try {
          await apiClient.post(`/relay/tokens/my/${id}/revoke`);
          message.success('已吊销');
          loadTokens();
        } catch (e) {
          message.error(extractApiError(e, '吊销失败'));
        }
      },
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => message.success('已复制'));
  };

  const columns = [
    { title: '名称', dataIndex: 'label', key: 'label' },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: (s: string) => {
        const color = s === 'active' ? 'green' : s === 'expired' ? 'orange' : 'red';
        return <Tag color={color}>{s === 'active' ? '启用' : s}</Tag>;
      },
    },
    {
      title: '配额(tokens)', key: 'quota',
      render: (_: any, r: RelayTokenInfo) => (
        <span>{r.quotaUsed.toLocaleString()} / {r.quotaTotal === 0 ? '不限' : r.quotaTotal.toLocaleString()}</span>
      ),
    },
    {
      title: '过期时间', dataIndex: 'expireAt', key: 'expireAt',
      render: (d: string | null) => (d ? new Date(d).toLocaleDateString() : '永久'),
    },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', render: (d: string) => new Date(d).toLocaleDateString() },
    {
      title: '操作', key: 'actions',
      render: (_: any, r: RelayTokenInfo) => (
        r.status === 'active' ? (
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => revokeToken(r.id)}>吊销</Button>
        ) : <span style={{ color: '#999' }}>已失效</span>
      ),
    },
  ];

  const usageColumns = [
    { title: '模型', dataIndex: '_id', key: '_id', render: (m: string) => m || '(未记录)' },
    { title: '调用次数', dataIndex: 'calls', key: 'calls' },
    { title: '消耗 tokens', dataIndex: 'used', key: 'used' },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="中转站令牌用于 OpenAI 兼容客户端(Codex / Cursor / OpenWebUI 等)"
        description={
          <span>
            接入地址:{' '}
            <code>{RELAY_BASE_URL}</code>,认证方式 <code>Authorization: Bearer &lt;令牌&gt;</code>。
          </span>
        }
      />
      <Card
        style={{ marginBottom: 16 }}
        title={<span><KeyOutlined /> 我的令牌</span>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>创建令牌</Button>}
      >
        <Table
          dataSource={tokens}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={false}
          size="middle"
          locale={{ emptyText: '暂无令牌,点击右上角创建' }}
        />
      </Card>

      <Card title={<span><BarChartOutlined /> 用量概览(按模型)</span>} loading={usageLoading}>
        {usage.length === 0 ? (
          <Empty description="暂无用量记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table dataSource={usage} columns={usageColumns} rowKey="_id" pagination={false} size="small" />
        )}
      </Card>

      {/* 创建令牌弹窗 */}
      <Modal
        title="创建中转站令牌"
        open={createModalOpen}
        onOk={createToken}
        onCancel={() => setCreateModalOpen(false)}
        okText="创建"
        confirmLoading={creating}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Paragraph type="secondary">
            创建后令牌仅显示一次,请立即保存。配额上限默认 50 万 tokens(按环境配置)。
          </Paragraph>
          <Input
            placeholder="备注名称(可选,如:我的 Codex)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            maxLength={50}
          />
        </Space>
      </Modal>

      {/* 新令牌展示弹窗(仅一次) */}
      <Modal
        title="令牌创建成功"
        open={newTokenModalOpen}
        onOk={() => { setNewTokenModalOpen(false); setNewTokenPlain(''); }}
        onCancel={() => { setNewTokenModalOpen(false); setNewTokenPlain(''); }}
        okText="我已复制保存"
      >
        <Paragraph type="warning" strong>
          ⚠️ 此令牌仅显示一次,请立即复制保存!
        </Paragraph>
        <Input
          value={newTokenPlain}
          readOnly
          addonAfter={<Tooltip title="复制"><CopyOutlined onClick={() => copyToClipboard(newTokenPlain)} style={{ cursor: 'pointer' }} /></Tooltip>}
        />
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Statistic title="接入地址" value={RELAY_BASE_URL} valueStyle={{ fontSize: 13 }} />
          </Col>
          <Col span={12}>
            <Statistic title="使用示例" value="curl -H Authorization: Bearer <令牌>" valueStyle={{ fontSize: 13 }} />
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default RelayTokensTab;